import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaBed, FaWifi, FaTv, FaSwimmingPool, FaFutbol, FaRegSnowflake, FaMugHot, FaChair } from 'react-icons/fa';
import { supabase } from '../lib/supabaseClient';
import FullHouseCard from './FullHouseCard';
import ImageCarousel from './ImageCarousel';

const initialRoomData = [
  {
    name: 'Habitación Matrimonial',
    images: [
      '/images/matrimonial-1.jpeg',
      '/images/matrimonial-2.jpeg',
      '/images/matrimonial-3.jpeg',
    ],
    price: 'S/ 250',
    description: 'Ideal para parejas, esta habitación ofrece el máximo confort y una vista privilegiada.',
    features: [
      { icon: <FaBed />, text: 'Cama King' },
      { icon: <FaTv />, text: 'Televisión' },
      { icon: <FaRegSnowflake />, text: 'Aire Acondicionado' },
      { icon: <FaWifi />, text: 'Wifi de alta velocidad' },
      { icon: <FaMugHot />, text: 'Incluye Desayuno' },
      { icon: <FaSwimmingPool />, text: 'Acceso a Piscina y Canchas' },
    ],
    details: 'Además, cuenta con frigobar, sofá y un refresco de cortesía a su llegada.'
  },
  {
    name: 'Habitación Doble',
    images: [
      '/images/doble-1.jpeg',
      '/images/doble-2.jpeg',
      '/images/doble-3.jpeg',
    ],
    price: 'S/ 220',
    description: 'Perfecta para amigos o familia, equipada con todo lo necesario para una estancia placentera.',
    features: [
      { icon: <FaBed />, text: '2 Camas de 2 Plazas' },
      { icon: <FaTv />, text: 'Televisión' },
      { icon: <FaChair />, text: 'Mesa y Silla de trabajo' },
      { icon: <FaWifi />, text: 'Wifi de alta velocidad' },
      { icon: <FaMugHot />, text: 'Incluye Desayuno' },
      { icon: <FaFutbol />, text: 'Acceso a Piscina y Canchas' },
    ],
    details: ''
  }
];

const cardItemVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};

const RoomCard = ({ room, onReserveClick }) => {
  return (
    <motion.div
      variants={cardItemVariants}
      whileHover={{ y: -8, transition: { duration: 0.3 } }}
      className="bg-white rounded-2xl overflow-hidden flex flex-col h-full"
      style={{
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        transition: 'box-shadow 0.3s ease',
      }}
      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 0, 0, 0.15)'}
      onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)'}
    >
      <div className="relative">
        <ImageCarousel images={room.images} />
        <div
          className="absolute top-3 right-3 text-white py-1.5 px-4 rounded-full font-bold z-10 text-sm"
          style={{
            background: 'rgba(20, 184, 166, 0.9)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            boxShadow: '0 4px 15px rgba(20, 184, 166, 0.3)',
          }}
        >
          {room.price} <span className="text-xs font-normal opacity-90">/ noche</span>
        </div>
      </div>
      <div className="p-6 flex flex-col flex-grow">
        <h3 className="text-2xl font-playfair font-bold text-gray-800 mb-2">{room.name}</h3>
        <p className="text-gray-500 mb-5 flex-grow text-sm leading-relaxed">{room.description}</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 mb-5">
          {room.features.map((feature, i) => (
            <div key={i} className="flex items-center text-gray-600 text-sm">
              <span className="text-teal-500 mr-2 text-base">{feature.icon}</span>
              {feature.text}
            </div>
          ))}
        </div>
        {room.details && <p className="text-xs text-gray-400 mb-2">{room.details}</p>}
      </div>
      <div className="px-6 pb-6">
        <motion.button
          onClick={onReserveClick}
          whileHover={{ scale: 1.02, boxShadow: '0 10px 25px rgba(20, 184, 166, 0.3)' }}
          whileTap={{ scale: 0.98 }}
          className="w-full text-white py-3 px-6 text-sm font-semibold rounded-full transition-all duration-300"
          style={{ background: '#14B8A6' }}
        >
          Reservar Ahora
        </motion.button>
      </div>
    </motion.div>
  );
};

const Rooms = ({ onReserveClick }) => {
  const [roomData, setRoomData] = useState(initialRoomData);

  useEffect(() => {
    const fetchRoomPrices = async () => {
      const { data: dbRoomTypes, error } = await supabase
        .from('room_types')
        .select('name, price_per_night');

      if (error) {
        console.error('Error fetching room prices:', error);
        return;
      }

      const updatedRoomData = initialRoomData.map(room => {
        const dbRoom = dbRoomTypes.find(dbRt => dbRt.name === room.name);
        if (dbRoom) {
          return {
            ...room,
            price: `S/ ${parseFloat(dbRoom.price_per_night).toFixed(0)}`
          };
        }
        return room;
      });

      setRoomData(updatedRoomData);
    };

    fetchRoomPrices();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      }
    }
  };

  return (
    <section
      id="habitaciones"
      className="py-20 relative overflow-hidden"
      style={{ background: '#ffffff' }}
    >
      {/* Animated gradient background */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            'radial-gradient(ellipse at 20% 50%, rgba(20, 184, 166, 0.25) 0%, transparent 65%)',
            'radial-gradient(ellipse at 80% 50%, rgba(20, 184, 166, 0.25) 0%, transparent 65%)',
            'radial-gradient(ellipse at 50% 80%, rgba(20, 184, 166, 0.2) 0%, transparent 65%)',
            'radial-gradient(ellipse at 20% 50%, rgba(20, 184, 166, 0.25) 0%, transparent 65%)',
          ],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        style={{ zIndex: 1 }}
      />
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            'radial-gradient(ellipse at 70% 30%, rgba(196, 173, 121, 0.2) 0%, transparent 55%)',
            'radial-gradient(ellipse at 30% 70%, rgba(196, 173, 121, 0.2) 0%, transparent 55%)',
            'radial-gradient(ellipse at 60% 20%, rgba(196, 173, 121, 0.15) 0%, transparent 55%)',
            'radial-gradient(ellipse at 70% 30%, rgba(196, 173, 121, 0.2) 0%, transparent 55%)',
          ],
        }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
        style={{ zIndex: 1 }}
      />
      <div className="container mx-auto px-4 relative" style={{ zIndex: 2 }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-sm uppercase tracking-[0.3em] mb-3 font-medium"
            style={{ color: '#c4ad79' }}
          >
            Alojamiento
          </motion.p>
          <h2 className="text-4xl md:text-5xl font-playfair font-extrabold text-gray-900">Nuestras Habitaciones</h2>
          <p className="mt-4 text-base text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Confort y naturaleza se unen en nuestros espacios diseñados para tu descanso.
          </p>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
            className="mx-auto mt-5"
            style={{
              width: '60px',
              height: '2px',
              background: '#c4ad79',
              transformOrigin: 'center',
              borderRadius: '2px',
            }}
          />
        </motion.div>
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="-ml-4 pl-4 flex overflow-x-auto pb-6 horizontal-scrollbar snap-x snap-mandatory md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 lg:gap-10 md:pb-0 md:pl-0 md:ml-0 md:snap-none"
        >
          {roomData.map((room, index) => (
            <div key={room.name} className="ml-4 flex-shrink-0 w-[80vw] sm:w-[65vw] md:w-auto md:ml-0 snap-center">
              <RoomCard room={room} onReserveClick={onReserveClick} />
            </div>
          ))}
          <motion.div variants={cardItemVariants} className="ml-4 flex-shrink-0 w-[80vw] sm:w-[65vw] md:w-auto md:ml-0 snap-center">
            <FullHouseCard />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Rooms;
